/**
 * Analytic coverage badge status for the ICR Coverage tab.
 *
 * AR bands: <10% not covered, 10–90% partially covered, >90% fully covered.
 * Parent (question/EEI) rolls up from child AR statuses; ICR rolls up from
 * question + EEIs.
 */

import {PIR_STATUS} from '../pirPayload';

export const COVERAGE_BADGE = {
    FULLY: 'Fully covered',
    PARTIAL: 'Partially covered',
    NOT: 'Not covered',
};

/** AR / leaf coverage % → PIR_STATUS. */
export function arCoverageStatus(coveragePct) {
    if (coveragePct == null || Number.isNaN(Number(coveragePct))) {
        return PIR_STATUS.UNCOVERED;
    }
    const pct = Number(coveragePct);
    if (pct < 10) return PIR_STATUS.UNCOVERED;
    if (pct > 90) return PIR_STATUS.COVERED;
    return PIR_STATUS.PARTIAL;
}

/**
 * Question / EEI status from AR statuses.
 * Fully = all ARs fully covered.
 * Not covered = no AR is fully or partially covered.
 * Partially = otherwise (includes ≥1 partial, or a mix of fully + not covered).
 */
export function parentCoverageStatusFromChildren(childStatuses) {
    const statuses = (childStatuses || []).filter(Boolean);
    if (statuses.length === 0) {
        return PIR_STATUS.UNCOVERED;
    }
    if (statuses.every((s) => s === PIR_STATUS.COVERED)) {
        return PIR_STATUS.COVERED;
    }
    if (statuses.every((s) => s === PIR_STATUS.UNCOVERED)) {
        return PIR_STATUS.UNCOVERED;
    }
    return PIR_STATUS.PARTIAL;
}

/**
 * ICR status from question + EEI statuses.
 * Fully = all fully covered.
 * Partially = at least one partially covered.
 * Not covered = otherwise.
 */
export function icrCoverageStatusFromChildren(childStatuses) {
    const statuses = (childStatuses || []).filter(Boolean);
    if (statuses.length === 0) {
        return PIR_STATUS.UNCOVERED;
    }
    if (statuses.every((s) => s === PIR_STATUS.COVERED)) {
        return PIR_STATUS.COVERED;
    }
    if (statuses.some((s) => s === PIR_STATUS.PARTIAL)) {
        return PIR_STATUS.PARTIAL;
    }
    return PIR_STATUS.UNCOVERED;
}

/** PIR_STATUS → Coverage-tab badge label. */
export function coverageBadgeLabel(status) {
    if (status === PIR_STATUS.COVERED) return COVERAGE_BADGE.FULLY;
    if (status === PIR_STATUS.PARTIAL) return COVERAGE_BADGE.PARTIAL;
    return COVERAGE_BADGE.NOT;
}

export function coverageBadgeColor(status) {
    if (status === PIR_STATUS.COVERED) return 'success';
    if (status === PIR_STATUS.PARTIAL) return 'warning';
    return 'default';
}

/**
 * Resolve status for a question/EEI: prefer AR rollup when ARs exist,
 * otherwise fall back to the leaf % bands.
 */
export function resolveRequirementCoverageStatus(ars, coveragePct) {
    const list = Array.isArray(ars) ? ars : [];
    if (list.length > 0) {
        return parentCoverageStatusFromChildren(
            list.map((ar) => arCoverageStatus(ar?.coveragePct)),
        );
    }
    return arCoverageStatus(coveragePct);
}
